import React, {
  useMemo,
  type HTMLAttributes,
  type MutableRefObject,
  type RefObject,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { RecursiveMenu, RecursiveMenuTransitions } from '@uncinc/react-drupal-core';
import { Container, Lottie, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import type { LottieItem } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Lottie/types';

import { ButtonWrapper, MaxMenu } from '../../../../components';
import { SCREEN_LG } from '../../../../config/constants';
import SoloSearch from '../../../../components/Search/SoloSearch';

import hamburgerSearchLottie from '../../../../assets/lotties/hamburger-search.json';
import Logo from '../../Logo';

import './style.scss';

interface MenuButtonProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  lottieRef: RefObject<LottieItem>;
  isOverlayOpen: MutableRefObject<boolean>;
}

const MenuButton: React.FC<MenuButtonProps> = React.memo(({
  lottieRef,
  isOverlayOpen,
}) => (
  <Lottie
    id="hamburgerOpen"
    ref={lottieRef}
    data={hamburgerSearchLottie}
    autoPlay={false}
    loop={false}
    direction={1}
    onInit={(instance) => {
      instance.addEventListener('complete', () => {
        instance.setDirection(isOverlayOpen.current ? -1 : 1);
      });
    }}
  />
));

interface HeaderProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  isOverlayOpen: MutableRefObject<boolean>;
  toggleOverlay: (arg0?: boolean) => void;
  headerRef: RefObject<HTMLDivElement>;
  lottieRef: RefObject<LottieItem>;
}

const Header: React.FC<HeaderProps> = ({
  isOverlayOpen,
  toggleOverlay,
  headerRef,
  lottieRef,
}) => {
  const { t } = useTranslation();

  const collapseAll = RecursiveMenuTransitions.useCollapseSubmenus();
  const currentItem = RecursiveMenuTransitions.useExpandedSubmenu();

  const { width } = useWindowDimensions();

  const isScreenLarge = useMemo(() => width >= SCREEN_LG, [width]);

  return (
    <div className="HeaderMenu__Header">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={classNames([
          'HeaderMenu__Header__overlay',
          `HeaderMenu__Header__overlay--${currentItem ? 'visible' : 'hidden'}`,
        ])}
        onClick={() => collapseAll()}
      />

      <div className="HeaderMenu__Header__background">
        <Container className="HeaderMenu__Header__wrapper">
          <Logo
            onClick={() => {
              toggleOverlay(true);
              collapseAll();
            }}
          />

          <div className="HeaderMenu__Header__inner">
            <div
              ref={headerRef}
              className="HeaderMenu__Header__menus"
            >
              <MaxMenu
                menuId="main"
                className="HeaderMenu__Header__menu HeaderMenu__Header__menu--main"
                onClickFunction={() => {
                  toggleOverlay(true);
                  collapseAll();
                }}
              />
            </div>

            <div className="HeaderMenu__Header__actions">
              <RecursiveMenu
                menuId="navigation-actions"
                maxLevel={1}
                className="HeaderMenu__Header__menu HeaderMenu__Header__menu--actions"
                onClickFunction={() => {
                  toggleOverlay(true);
                  collapseAll();
                }}
              />

              {isScreenLarge && (
                <SoloSearch collapsible />
              )}

              {!isScreenLarge && (
                <ButtonWrapper
                  className="HeaderMenu__Header__hamburger"
                  tabIndex={0}
                  type="textual"
                  onClick={() => {
                    toggleOverlay();

                    if (currentItem !== null) {
                      collapseAll();
                    }
                  }}
                >
                  <div className="HeaderMenu__Header__hamburger__label">
                    <span className="HeaderMenu__Header__hamburger__label__state">{t('general.close')}</span>
                    <span className="HeaderMenu__Header__hamburger__label__state">{t('general.menu')}</span>
                  </div>
                  <div className="HeaderMenu__Header__hamburger__button">
                    <MenuButton
                      lottieRef={lottieRef}
                      isOverlayOpen={isOverlayOpen}
                    />
                  </div>
                </ButtonWrapper>
              )}
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Header;
